import { c as _c } from "react/compiler-runtime";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { api, type BoutiqueConfig, type Product, type ProductQuery, type ProductTypeKey, type Shop } from "@/api";
import { ChevronLeftIcon, ChevronRightIcon, EyeIcon, Filter16Icon, SearchIcon, SortIcon, StarIcon } from "@/components/icons";
import { ProductCard } from "@/components/product/product-card";
import { Button } from "@/components/ui/button";
import { Chip } from "@/components/ui/chip";
import { Segmented } from "@/components/ui/segmented";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/text-field";
import { expandProductTiles } from "@/lib/product-tiles";
import { cn } from "@/lib/utils";

// Boutique storefront primitives — the buyer-facing counterpart of the seller
// preview (apps/seller-web/components/boutique/*). Everything here renders from
// the published `BoutiqueConfig` so what a seller set up in the constructor is
// what the buyer sees.

/** Вкладки витрины (fd-boutique-02): «Главная», категории `cat:<id>`, «Идеи»,
 *  «Индивидуальный заказ», «О магазине». */
export type ShopTabKey = "main" | "ideas" | "custom" | "about" | `cat:${string}`;
// fd-boutique-06: «Новинки» — по дате появления товара.
export type SortKey = "popular" | "newest" | "price-asc" | "price-desc";
/** Фильтры ленты магазина — применяются на шлюзе (/catalog/search). */
export type StoreFilters = {
  categoryId?: string;
  collectionId?: string;
  color?: string;
  size?: string;
  priceMin?: number;
  priceMax?: number;
  search?: string;
  productTypes?: ProductTypeKey[];
  sale?: boolean;
};
export const SORT_KEYS: SortKey[] = ["popular", "newest", "price-asc", "price-desc"];
export const PRODUCT_TYPE_KEYS: ProductTypeKey[] = ["in-stock", "customizable", "made-to-order"];

/** Фильтры и сортировка витрины → запрос каталога шлюза. */
export const toProductQuery = (shopId: string, filters: StoreFilters, sort: SortKey): ProductQuery => ({
  shopId,
  categoryIds: filters.categoryId ? [filters.categoryId] : undefined,
  collectionId: filters.collectionId,
  colors: filters.color ? [filters.color] : undefined,
  sizes: filters.size ? [filters.size] : undefined,
  priceMin: filters.priceMin,
  priceMax: filters.priceMax,
  search: filters.search?.trim() || undefined,
  productTypes: filters.productTypes?.length ? filters.productTypes : undefined,
  sale: filters.sale || undefined,
  sort
});
export const countFilters = (f: StoreFilters): number => (f.categoryId ? 1 : 0) + (f.collectionId ? 1 : 0) + (f.size ? 1 : 0) + (f.color ? 1 : 0) + (f.priceMin != null || f.priceMax != null ? 1 : 0) + (f.search?.trim() ? 1 : 0) + (f.productTypes?.length ? 1 : 0) + (f.sale ? 1 : 0);
type BannerSlide = BoutiqueConfig["banner"]["slides"][number];
type AboutBlock = BoutiqueConfig["about"]["blocks"][number];

/** Узкий экран (телефон): сетка 1/2 колонки вместо 2/4/6. */
export function useNarrow() {
  const $ = _c(2);
  const [narrow, setNarrow] = useState(false);
  let t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = () => {
      const mq = window.matchMedia("(max-width: 768px)");
      const update = () => setNarrow(mq.matches);
      update();
      mq.addEventListener("change", update);
      return () => mq.removeEventListener("change", update);
    };
    t1 = [];
    $[0] = t0;
    $[1] = t1;
  } else {
    t0 = $[0];
    t1 = $[1];
  }
  useEffect(t0, t1);
  return narrow;
}

// ── Banner slider (fd-boutique-03) ───────────────────────────────────────────
// Autoplaying carousel with clickable dot indicators and prev/next controls.
// `variant` picks the two Figma looks: an image hero with an overlaid caption,
// or a large-text display banner.
export function BannerSlider(t0) {
  const $ = _c(17);
  const {
    slides,
    variant: t1,
    fallbackImage
  } = t0;
  const variant = t1 === undefined ? "hero" : t1;
  const [idx, setIdx] = useState(0);
  const usable = slides.filter(_temp);
  const count = usable.length;
  let t2;
  let t3;
  if ($[0] !== count) {
    t2 = () => {
      if (count < 2) {
        return;
      }
      const id = setInterval(() => setIdx(i => (i + 1) % count), 5000);
      return () => clearInterval(id);
    };
    t3 = [count];
    $[0] = count;
    $[1] = t2;
    $[2] = t3;
  } else {
    t2 = $[1];
    t3 = $[2];
  }
  useEffect(t2, t3);
  let t4;
  let t5;
  if ($[3] !== count || $[4] !== idx) {
    t4 = () => {
      if (idx >= count && count > 0) {
        setIdx(0);
      }
    };
    t5 = [idx, count];
    $[3] = count;
    $[4] = idx;
    $[5] = t4;
    $[6] = t5;
  } else {
    t4 = $[5];
    t5 = $[6];
  }
  useEffect(t4, t5);
  if (count === 0) {
    if (!fallbackImage) {
      return null;
    }
    let t6;
    if ($[7] !== fallbackImage) {
      t6 = <div className="relative aspect-[21/8] w-full overflow-hidden rounded-none bg-osmix-gray-100 md:aspect-[15/4]"><img src={fallbackImage} alt="" className="size-full object-cover" /></div>;
      $[7] = fallbackImage;
      $[8] = t6;
    } else {
      t6 = $[8];
    }
    return t6;
  }
  const slide = usable[Math.min(idx, count - 1)];
  if (!slide) {
    return null;
  }
  let t6;
  if ($[9] !== count) {
    t6 = n => setIdx((n % count + count) % count);
    $[9] = count;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  const go = t6;
  const t7 = variant === "hero" ? "aspect-[21/8] rounded-none md:aspect-[15/4]" : "aspect-[21/8] rounded-2xl";
  let t8;
  if ($[11] !== t7) {
    t8 = cn("group relative w-full overflow-hidden bg-osmix-gray-100", t7);
    $[11] = t7;
    $[12] = t8;
  } else {
    t8 = $[12];
  }
  let t9;
  if ($[13] !== count || $[14] !== go || $[15] !== idx) {
    t9 = count > 1 && <><button type="button" aria-label="prev" onClick={() => go(idx - 1)} className="absolute left-3 top-1/2 flex size-9 -translate-y-1/2 items-center justify-center rounded-full bg-white/80 text-osmix-text-1 opacity-0 transition group-hover:opacity-100 hover:bg-white"><ChevronLeftIcon size={20} /></button><button type="button" aria-label="next" onClick={() => go(idx + 1)} className="absolute right-3 top-1/2 flex size-9 -translate-y-1/2 items-center justify-center rounded-full bg-white/80 text-osmix-text-1 opacity-0 transition group-hover:opacity-100 hover:bg-white"><ChevronRightIcon size={20} /></button></>;
    $[13] = count;
    $[14] = go;
    $[15] = idx;
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  return <div className="flex flex-col items-center gap-4"><div className={t8}>{slide.imageUrl && <img src={slide.imageUrl} alt={slide.title ?? ""} className="size-full object-cover transition-opacity duration-500" />}{variant === "hero" ? (slide.title || slide.description) && <><div className="absolute inset-0 bg-gradient-to-t from-osmix-text-1/60 to-transparent" /><div className="absolute bottom-0 left-0 flex flex-col gap-2 px-4 py-6 text-white md:px-8 md:py-10">{slide.title && <h2 className="type-h1-bold text-white!">{slide.title}</h2>}{slide.description && <p className="max-w-2xl type-body1 text-white/90">{slide.description}</p>}</div></> : slide.title && <div className="absolute inset-0 flex items-center justify-center p-8"><div className="absolute inset-0 bg-osmix-text-1/35" /><h2 className="relative text-center font-bold uppercase leading-none tracking-tight text-white drop-shadow-lg" style={{
          fontSize: "clamp(40px, 9vw, 140px)",
          textShadow: "0 2px 24px rgba(0,0,0,0.45)"
        }}>{slide.title}</h2></div>}{t9}</div>{count > 1 && <div className="flex items-center gap-2">{usable.map((s_0, i_0) => <button key={s_0.id} type="button" aria-label={`slide ${i_0 + 1}`} onClick={() => setIdx(i_0)} className={cn("h-1.5 rounded-full transition-all", i_0 === idx ? "w-8 bg-osmix-tangelo" : "w-3 bg-osmix-gray-300 hover:bg-osmix-icon")} />)}</div>}</div>;
}

// ── Вкладки витрины (fd-boutique-02, Figma 3870:27122) ───────────────────────
// «Главная» + категории магазина + «★ Идеи для вдохновения» + «Индивидуальный
// заказ» (если продавец принимает) + «О магазине». Табы 16/20 #1F1F1F с шагом
// 20; активный — w600 и подчёркивание 1,5 px в 2 px под строкой.
function _temp(s) {
  return s.imageUrl || s.title;
}
export function ShopNavTabs(t0) {
  const $ = _c(35);
  const {
    categories,
    active,
    customEnabled,
    onChange
  } = t0;
  const {
    t
  } = useTranslation();
  const item = _temp2;
  let t1;
  if ($[0] !== active || $[1] !== onChange) {
    t1 = (key, label, extra) => <button key={key} type="button" role="tab" aria-selected={active === key} className={cn(item(active === key), extra)} onClick={() => onChange(key)}>{label}</button>;
    $[0] = active;
    $[1] = onChange;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const tab = t1;
  let t2;
  if ($[3] !== t) {
    t2 = t("shop.tabsLabel");
    $[3] = t;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== t || $[6] !== tab) {
    t3 = tab("main", t("shop.tabs.main"));
    $[5] = t;
    $[6] = tab;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] !== categories || $[9] !== tab) {
    let t5;
    if ($[11] !== tab) {
      t5 = c => tab(`cat:${c.id}`, c.label);
      $[11] = tab;
      $[12] = t5;
    } else {
      t5 = $[12];
    }
    t4 = categories.map(t5);
    $[8] = categories;
    $[9] = tab;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  let t5;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <StarIcon className="text-ds-accent" />;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  let t6;
  if ($[14] !== t) {
    t6 = t("shop.ideas");
    $[14] = t;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  let t7;
  if ($[16] !== t6) {
    t7 = <>{t5}{t6}</>;
    $[16] = t6;
    $[17] = t7;
  } else {
    t7 = $[17];
  }
  let t8;
  if ($[18] !== t7 || $[19] !== tab) {
    t8 = tab("ideas", t7, "flex items-center gap-1.5");
    $[18] = t7;
    $[19] = tab;
    $[20] = t8;
  } else {
    t8 = $[20];
  }
  let t9;
  if ($[21] !== customEnabled || $[22] !== t || $[23] !== tab) {
    t9 = customEnabled && tab("custom", t("shop.tabs.custom"));
    $[21] = customEnabled;
    $[22] = t;
    $[23] = tab;
    $[24] = t9;
  } else {
    t9 = $[24];
  }
  let t10;
  if ($[25] !== t || $[26] !== tab) {
    t10 = tab("about", t("shop.tabs.aboutShop"));
    $[25] = t;
    $[26] = tab;
    $[27] = t10;
  } else {
    t10 = $[27];
  }
  let t11;
  if ($[28] !== t10 || $[29] !== t2 || $[30] !== t3 || $[31] !== t4 || $[32] !== t8 || $[33] !== t9) {
    t11 = <nav aria-label={t2} role="tablist" className="-mx-4 flex items-center gap-5 overflow-x-auto px-4 pb-1 [scrollbar-width:none] md:mx-0 md:flex-wrap md:gap-y-3 md:overflow-visible md:px-0" data-testid="shop-tabs">{t3}{t4}{t8}{t9}{t10}</nav>;
    $[28] = t10;
    $[29] = t2;
    $[30] = t3;
    $[31] = t4;
    $[32] = t8;
    $[33] = t9;
    $[34] = t11;
  } else {
    t11 = $[34];
  }
  return t11;
}

// ── Filter chip used in the shop filter bar (mirrors the catalog bar) ─────────
// DS `Chip` sm: обычный — #1F1F1F 3 % + обводка 4 %, активный — тангело 5 % с
// обводкой и крестиком сброса.
function _temp2(isActive) {
  return cn("relative whitespace-nowrap rounded-[4px] text-[16px] leading-5 text-[#1F1F1F] outline-none transition-colors focus-visible:ring-2 focus-visible:ring-ds-focus", isActive ? "font-semibold tracking-[-0.31px] after:absolute after:inset-x-0 after:top-[calc(100%+2px)] after:h-[1.5px] after:bg-[#1F1F1F]" : "font-normal hover:text-osmix-text-2");
}
function FilterPill(t0) {
  const $ = _c(5);
  const {
    label,
    active,
    onOpen,
    onClear
  } = t0;
  const t1 = Boolean(active);
  const t2 = active ? onClear : undefined;
  let t3;
  if ($[0] !== label || $[1] !== onOpen || $[2] !== t1 || $[3] !== t2) {
    t3 = <Chip selected={t1} onClick={onOpen} onRemove={t2} removeLabel={label}>{label}</Chip>;
    $[0] = label;
    $[1] = onOpen;
    $[2] = t1;
    $[3] = t2;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  return t3;
}

// ── Shop filter bar: dark «Фильтры» pill + quick chips + columns + sort ───────
// Figma-бар каталога. Фильтры уходят на шлюз (лента страницами): тёмная
// «Фильтры» раскрывает панель с поиском, коллекцией, типом товара, SALE,
// категорией, размером, цветом и ценой (fd-boutique-05); справа —
// переключатель сетки 2/4/6 (на телефоне 1/2, fd-boutique-04) и сортировка
// с «Новинками» (fd-boutique-06).
export function ShopFilterBar(t0) {
  const $ = _c(158);
  const {
    sort,
    onSortChange,
    filters,
    onFiltersChange,
    categories,
    collections,
    colors,
    sizes,
    priceBounds,
    columns,
    onColumnsChange,
    lockCategory
  } = t0;
  const {
    t
  } = useTranslation();
  const [open, setOpen] = useState(false);
  let t1;
  if ($[0] !== filters || $[1] !== onFiltersChange) {
    t1 = patch => onFiltersChange({
      ...filters,
      ...patch
    });
    $[0] = filters;
    $[1] = onFiltersChange;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const set = t1;
  const priceActive = filters.priceMin != null || filters.priceMax != null;
  let t2;
  if ($[3] !== filters || $[4] !== lockCategory) {
    t2 = lockCategory ? {
      ...filters,
      categoryId: undefined
    } : filters;
    $[3] = filters;
    $[4] = lockCategory;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const activeCount = countFilters(t2);
  let T0;
  let categoryLabel;
  let numOrUndef;
  let priceLabel;
  let sortLabel;
  let t3;
  let t4;
  let t5;
  let t6;
  let t7;
  let t8;
  let toggleType;
  let typeLabel;
  let types;
  if ($[6] !== activeCount || $[7] !== categories || $[8] !== collections || $[9] !== filters.categoryId || $[10] !== filters.collectionId || $[11] !== filters.priceMax || $[12] !== filters.priceMin || $[13] !== filters.productTypes || $[14] !== open || $[15] !== priceActive || $[16] !== set || $[17] !== t) {
    types = filters.productTypes ?? [];
    toggleType = k => set({
      productTypes: types.includes(k) ? types.filter(x => x !== k) : [...types, k]
    });
    let t9;
    if ($[32] !== t) {
      t9 = s => t(`shop.sort.${s === "price-asc" ? "priceAsc" : s === "price-desc" ? "priceDesc" : s === "newest" ? "newest" : "popular"}`);
      $[32] = t;
      $[33] = t9;
    } else {
      t9 = $[33];
    }
    sortLabel = t9;
    let t10;
    if ($[34] !== t) {
      t10 = k_0 => t(`catalogFilters.type.${k_0 === "in-stock" ? "inStock" : k_0 === "customizable" ? "customizable" : "madeToOrder"}`);
      $[34] = t;
      $[35] = t10;
    } else {
      t10 = $[35];
    }
    typeLabel = t10;
    let t11;
    if ($[36] !== filters.priceMax || $[37] !== filters.priceMin || $[38] !== priceActive || $[39] !== t) {
      t11 = priceActive ? filters.priceMin != null && filters.priceMax != null ? `${filters.priceMin} – ${filters.priceMax}` : filters.priceMin != null ? `${t("filters.priceFrom")} ${filters.priceMin}` : `${t("filters.priceTo")} ${filters.priceMax}` : t("filters.price");
      $[36] = filters.priceMax;
      $[37] = filters.priceMin;
      $[38] = priceActive;
      $[39] = t;
      $[40] = t11;
    } else {
      t11 = $[40];
    }
    priceLabel = t11;
    let t12;
    if ($[41] !== collections || $[42] !== filters.collectionId) {
      t12 = collections.find(c => c.id === filters.collectionId)?.title;
      $[41] = collections;
      $[42] = filters.collectionId;
      $[43] = t12;
    } else {
      t12 = $[43];
    }
    const collectionTitle = t12;
    let t13;
    if ($[44] !== categories || $[45] !== filters.categoryId) {
      t13 = categories.find(c_0 => c_0.id === filters.categoryId)?.label;
      $[44] = categories;
      $[45] = filters.categoryId;
      $[46] = t13;
    } else {
      t13 = $[46];
    }
    categoryLabel = t13;
    numOrUndef = _temp3;
    t7 = "flex flex-col gap-4";
    t8 = "shop-filter-bar";
    t4 = "flex flex-wrap items-center gap-x-4 gap-y-3";
    let t14;
    if ($[47] === Symbol.for("react.memo_cache_sentinel")) {
      t14 = () => setOpen(_temp4);
      $[47] = t14;
    } else {
      t14 = $[47];
    }
    let t15;
    if ($[48] === Symbol.for("react.memo_cache_sentinel")) {
      t15 = <Filter16Icon />;
      $[48] = t15;
    } else {
      t15 = $[48];
    }
    let t16;
    if ($[49] !== t) {
      t16 = t("filters.title");
      $[49] = t;
      $[50] = t16;
    } else {
      t16 = $[50];
    }
    let t17;
    if ($[51] !== activeCount) {
      t17 = activeCount > 0 && <span className="ml-0.5 flex size-5 items-center justify-center rounded-full bg-white text-[11px] font-bold text-ds-primary">{activeCount}</span>;
      $[51] = activeCount;
      $[52] = t17;
    } else {
      t17 = $[52];
    }
    if ($[53] !== open || $[54] !== t16 || $[55] !== t17) {
      t5 = <Button size="md" onClick={t14} aria-expanded={open} className="bg-ds-ink-2 hover:bg-ds-primary">{t15}{t16}{t17}</Button>;
      $[53] = open;
      $[54] = t16;
      $[55] = t17;
      $[56] = t5;
    } else {
      t5 = $[56];
    }
    if ($[57] !== collectionTitle || $[58] !== collections.length || $[59] !== filters.collectionId || $[60] !== set || $[61] !== t) {
      t6 = collections.length > 0 && <FilterPill label={collectionTitle ?? t("shop.filters.collection")} active={Boolean(filters.collectionId)} onOpen={() => setOpen(true)} onClear={() => set({
        collectionId: undefined
      })} />;
      $[57] = collectionTitle;
      $[58] = collections.length;
      $[59] = filters.collectionId;
      $[60] = set;
      $[61] = t;
      $[62] = t6;
    } else {
      t6 = $[62];
    }
    T0 = FilterPill;
    t3 = types.length ? `${t("shop.filters.type")} · ${types.map(typeLabel).join(", ")}` : t("shop.filters.type");
    $[6] = activeCount;
    $[7] = categories;
    $[8] = collections;
    $[9] = filters.categoryId;
    $[10] = filters.collectionId;
    $[11] = filters.priceMax;
    $[12] = filters.priceMin;
    $[13] = filters.productTypes;
    $[14] = open;
    $[15] = priceActive;
    $[16] = set;
    $[17] = t;
    $[18] = T0;
    $[19] = categoryLabel;
    $[20] = numOrUndef;
    $[21] = priceLabel;
    $[22] = sortLabel;
    $[23] = t3;
    $[24] = t4;
    $[25] = t5;
    $[26] = t6;
    $[27] = t7;
    $[28] = t8;
    $[29] = toggleType;
    $[30] = typeLabel;
    $[31] = types;
  } else {
    T0 = $[18];
    categoryLabel = $[19];
    numOrUndef = $[20];
    priceLabel = $[21];
    sortLabel = $[22];
    t3 = $[23];
    t4 = $[24];
    t5 = $[25];
    t6 = $[26];
    t7 = $[27];
    t8 = $[28];
    toggleType = $[29];
    typeLabel = $[30];
    types = $[31];
  }
  const t9 = types.length > 0;
  let t10;
  if ($[63] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = () => setOpen(true);
    $[63] = t10;
  } else {
    t10 = $[63];
  }
  let t11;
  if ($[64] !== set) {
    t11 = () => set({
      productTypes: undefined
    });
    $[64] = set;
    $[65] = t11;
  } else {
    t11 = $[65];
  }
  let t12;
  if ($[66] !== T0 || $[67] !== t11 || $[68] !== t3 || $[69] !== t9) {
    t12 = <T0 label={t3} active={t9} onOpen={t10} onClear={t11} />;
    $[66] = T0;
    $[67] = t11;
    $[68] = t3;
    $[69] = t9;
    $[70] = t12;
  } else {
    t12 = $[70];
  }
  const t13 = Boolean(filters.sale);
  let t14;
  if ($[71] !== filters.sale || $[72] !== set) {
    t14 = () => set({
      sale: filters.sale ? undefined : true
    });
    $[71] = filters.sale;
    $[72] = set;
    $[73] = t14;
  } else {
    t14 = $[73];
  }
  let t15;
  if ($[74] !== t13 || $[75] !== t14) {
    t15 = <Chip selected={t13} onClick={t14} data-testid="shop-filter-sale">SALE</Chip>;
    $[74] = t13;
    $[75] = t14;
    $[76] = t15;
  } else {
    t15 = $[76];
  }
  let t16;
  if ($[77] !== categories.length || $[78] !== categoryLabel || $[79] !== filters.categoryId || $[80] !== lockCategory || $[81] !== set || $[82] !== t) {
    t16 = !lockCategory && categories.length > 0 && <FilterPill label={categoryLabel ?? t("filters.category")} active={Boolean(filters.categoryId)} onOpen={() => setOpen(true)} onClear={() => set({
      categoryId: undefined
    })} />;
    $[77] = categories.length;
    $[78] = categoryLabel;
    $[79] = filters.categoryId;
    $[80] = lockCategory;
    $[81] = set;
    $[82] = t;
    $[83] = t16;
  } else {
    t16 = $[83];
  }
  let t17;
  if ($[84] !== filters.size || $[85] !== set || $[86] !== sizes.length || $[87] !== t) {
    t17 = sizes.length > 0 && <FilterPill label={filters.size ? `${t("filters.size")} · ${filters.size}` : t("filters.size")} active={Boolean(filters.size)} onOpen={() => setOpen(true)} onClear={() => set({
      size: undefined
    })} />;
    $[84] = filters.size;
    $[85] = set;
    $[86] = sizes.length;
    $[87] = t;
    $[88] = t17;
  } else {
    t17 = $[88];
  }
  let t18;
  if ($[89] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = () => setOpen(true);
    $[89] = t18;
  } else {
    t18 = $[89];
  }
  let t19;
  if ($[90] !== set) {
    t19 = () => set({
      priceMin: undefined,
      priceMax: undefined
    });
    $[90] = set;
    $[91] = t19;
  } else {
    t19 = $[91];
  }
  let t20;
  if ($[92] !== priceActive || $[93] !== priceLabel || $[94] !== t19) {
    t20 = <FilterPill label={priceLabel} active={priceActive} onOpen={t18} onClear={t19} />;
    $[92] = priceActive;
    $[93] = priceLabel;
    $[94] = t19;
    $[95] = t20;
  } else {
    t20 = $[95];
  }
  let t21;
  if ($[96] !== columns || $[97] !== onColumnsChange) {
    t21 = <GridColumnsSwitch value={columns} onChange={onColumnsChange} />;
    $[96] = columns;
    $[97] = onColumnsChange;
    $[98] = t21;
  } else {
    t21 = $[98];
  }
  let t22;
  if ($[99] !== onSortChange) {
    t22 = v_0 => onSortChange(v_0 as SortKey);
    $[99] = onSortChange;
    $[100] = t22;
  } else {
    t22 = $[100];
  }
  let t23;
  if ($[101] !== t) {
    t23 = t("shop.sort.label");
    $[101] = t;
    $[102] = t23;
  } else {
    t23 = $[102];
  }
  let t24;
  let t25;
  if ($[103] === Symbol.for("react.memo_cache_sentinel")) {
    t24 = <SelectValue />;
    t25 = <SortIcon className="size-6 text-ds-primary" />;
    $[103] = t24;
    $[104] = t25;
  } else {
    t24 = $[103];
    t25 = $[104];
  }
  let t26;
  if ($[105] !== t23) {
    t26 = <SelectTrigger aria-label={t23} className="w-auto gap-2 rounded-[4px] border-0 bg-transparent px-0 py-0 text-[16px] leading-[21px] font-medium text-ds-primary [&>svg:last-child]:hidden" data-testid="shop-sort">{t24}{t25}</SelectTrigger>;
    $[105] = t23;
    $[106] = t26;
  } else {
    t26 = $[106];
  }
  let t27;
  if ($[107] !== sortLabel) {
    t27 = SORT_KEYS.map(s_0 => <SelectItem key={s_0} value={s_0}>{sortLabel(s_0)}</SelectItem>);
    $[107] = sortLabel;
    $[108] = t27;
  } else {
    t27 = $[108];
  }
  let t28;
  if ($[109] !== t27) {
    t28 = <SelectContent align="end">{t27}</SelectContent>;
    $[109] = t27;
    $[110] = t28;
  } else {
    t28 = $[110];
  }
  let t29;
  if ($[111] !== sort || $[112] !== t22 || $[113] !== t26 || $[114] !== t28) {
    t29 = <Select value={sort} onValueChange={t22}>{t26}{t28}</Select>;
    $[111] = sort;
    $[112] = t22;
    $[113] = t26;
    $[114] = t28;
    $[115] = t29;
  } else {
    t29 = $[115];
  }
  let t30;
  if ($[116] !== t21 || $[117] !== t29) {
    t30 = <div className="ml-auto flex items-center gap-4">{t21}{t29}</div>;
    $[116] = t21;
    $[117] = t29;
    $[118] = t30;
  } else {
    t30 = $[118];
  }
  let t31;
  if ($[119] !== t12 || $[120] !== t15 || $[121] !== t16 || $[122] !== t17 || $[123] !== t20 || $[124] !== t30 || $[125] !== t4 || $[126] !== t5 || $[127] !== t6) {
    t31 = <div className={t4}>{t5}{t6}{t12}{t15}{t16}{t17}{t20}{t30}</div>;
    $[119] = t12;
    $[120] = t15;
    $[121] = t16;
    $[122] = t17;
    $[123] = t20;
    $[124] = t30;
    $[125] = t4;
    $[126] = t5;
    $[127] = t6;
    $[128] = t31;
  } else {
    t31 = $[128];
  }
  let t32;
  if ($[129] !== activeCount || $[130] !== categories || $[131] !== collections || $[132] !== colors || $[133] !== filters.categoryId || $[134] !== filters.collectionId || $[135] !== filters.color || $[136] !== filters.priceMax || $[137] !== filters.priceMin || $[138] !== filters.sale || $[139] !== filters.search || $[140] !== filters.size || $[141] !== lockCategory || $[142] !== numOrUndef || $[143] !== onFiltersChange || $[144] !== open || $[145] !== priceBounds || $[146] !== set || $[147] !== sizes || $[148] !== t || $[149] !== toggleType || $[150] !== typeLabel || $[151] !== types) {
    t32 = open && <div className="flex flex-col gap-5 rounded-[16px] bg-ds-quaternary p-5" data-testid="shop-filter-panel"><Input type="search" value={filters.search ?? ""} onChange={e => set({
        search: e.target.value || undefined
      })} placeholder={t("shop.searchPlaceholder")} aria-label={t("shop.searchPlaceholder")} start={<SearchIcon />} fieldClassName="bg-white" />{collections.length > 0 && <FilterGroup label={t("shop.filters.collection")}>{collections.map(c_1 => <ChoiceChip key={c_1.id} label={c_1.title} active={filters.collectionId === c_1.id} onClick={() => set({
          collectionId: filters.collectionId === c_1.id ? undefined : c_1.id
        })} />)}</FilterGroup>}<FilterGroup label={t("shop.filters.type")}>{PRODUCT_TYPE_KEYS.map(k_1 => <ChoiceChip key={k_1} label={typeLabel(k_1)} active={types.includes(k_1)} onClick={() => toggleType(k_1)} />)}<ChoiceChip label={t("shop.filters.saleOnly")} active={Boolean(filters.sale)} onClick={() => set({
          sale: filters.sale ? undefined : true
        })} /></FilterGroup>{!lockCategory && categories.length > 0 && <FilterGroup label={t("filters.category")}>{categories.map(c_2 => <ChoiceChip key={c_2.id} label={c_2.label} active={filters.categoryId === c_2.id} onClick={() => set({
          categoryId: filters.categoryId === c_2.id ? undefined : c_2.id
        })} />)}</FilterGroup>}{sizes.length > 0 && <FilterGroup label={t("filters.size")}>{sizes.map(s_1 => <ChoiceChip key={s_1} label={s_1} active={filters.size === s_1} onClick={() => set({
          size: filters.size === s_1 ? undefined : s_1
        })} />)}</FilterGroup>}{colors.length > 0 && <FilterGroup label={t("filters.color")}>{colors.map(c_3 => <ChoiceChip key={c_3} label={c_3} active={filters.color === c_3} onClick={() => set({
          color: filters.color === c_3 ? undefined : c_3
        })} />)}</FilterGroup>}<FilterGroup label={t("filters.price")}><div className="flex items-center gap-2"><Input variant="square" type="number" inputMode="numeric" value={filters.priceMin ?? ""} min={priceBounds.min} max={priceBounds.max} onChange={e_0 => set({
            priceMin: numOrUndef(e_0.target.value)
          })} placeholder={String(priceBounds.min)} aria-label={t("filters.priceFrom")} fieldClassName="w-28 bg-white" /><span className="text-ds-muted">–</span><Input variant="square" type="number" inputMode="numeric" value={filters.priceMax ?? ""} min={priceBounds.min} max={priceBounds.max} onChange={e_1 => set({
            priceMax: numOrUndef(e_1.target.value)
          })} placeholder={String(priceBounds.max)} aria-label={t("filters.priceTo")} fieldClassName="w-28 bg-white" /></div></FilterGroup>{activeCount > 0 && <Button variant="text" className="self-start" onClick={() => onFiltersChange(lockCategory ? {
        categoryId: filters.categoryId
      } : {})}>{t("filters.reset")}</Button>}</div>;
    $[129] = activeCount;
    $[130] = categories;
    $[131] = collections;
    $[132] = colors;
    $[133] = filters.categoryId;
    $[134] = filters.collectionId;
    $[135] = filters.color;
    $[136] = filters.priceMax;
    $[137] = filters.priceMin;
    $[138] = filters.sale;
    $[139] = filters.search;
    $[140] = filters.size;
    $[141] = lockCategory;
    $[142] = numOrUndef;
    $[143] = onFiltersChange;
    $[144] = open;
    $[145] = priceBounds;
    $[146] = set;
    $[147] = sizes;
    $[148] = t;
    $[149] = toggleType;
    $[150] = typeLabel;
    $[151] = types;
    $[152] = t32;
  } else {
    t32 = $[152];
  }
  let t33;
  if ($[153] !== t31 || $[154] !== t32 || $[155] !== t7 || $[156] !== t8) {
    t33 = <div className={t7} data-testid={t8}>{t31}{t32}</div>;
    $[153] = t31;
    $[154] = t32;
    $[155] = t7;
    $[156] = t8;
    $[157] = t33;
  } else {
    t33 = $[157];
  }
  return t33;
}
function _temp4(o) {
  return !o;
}
function _temp3(v) {
  return v.trim() === "" ? undefined : Number(v);
}
function FilterGroup(t0) {
  const $ = _c(7);
  const {
    label,
    children
  } = t0;
  let t1;
  if ($[0] !== label) {
    t1 = <span className="ds-h5 text-ds-primary">{label}</span>;
    $[0] = label;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== children) {
    t2 = <div className="flex flex-wrap gap-2">{children}</div>;
    $[2] = children;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== t1 || $[5] !== t2) {
    t3 = <div className="flex flex-col gap-2">{t1}{t2}</div>;
    $[4] = t1;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  return t3;
}
function ChoiceChip(t0) {
  const $ = _c(5);
  const {
    label,
    active,
    onClick
  } = t0;
  const t1 = active ? undefined : "bg-white";
  let t2;
  if ($[0] !== active || $[1] !== label || $[2] !== onClick || $[3] !== t1) {
    t2 = <Chip size="lg" selected={active} onClick={onClick} className={t1}>{label}</Chip>;
    $[0] = active;
    $[1] = label;
    $[2] = onClick;
    $[3] = t1;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  return t2;
}

// ── Переключатель сетки (fd-boutique-04) ─────────────────────────────────────
// DS «segmented»: на компьютере 2 / 4 / 6 колонок, на телефоне 1 / 2.
export const DESKTOP_COLUMNS = [2, 4, 6] as const;
export const MOBILE_COLUMNS = [1, 2] as const;
export function GridColumnsSwitch(t0) {
  const $ = _c(11);
  const {
    value,
    onChange
  } = t0;
  const {
    t
  } = useTranslation();
  const narrow = useNarrow();
  const t1 = narrow ? MOBILE_COLUMNS : DESKTOP_COLUMNS;
  let t2;
  if ($[0] !== t1) {
    t2 = t1.map(_temp5);
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const options = t2;
  const current = String(narrow ? Math.min(value, 2) : value < 2 ? 2 : value);
  let t3;
  if ($[2] !== t) {
    t3 = t("shop.columns");
    $[2] = t;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== onChange) {
    t4 = v => onChange(Number(v));
    $[4] = onChange;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== current || $[7] !== options || $[8] !== t3 || $[9] !== t4) {
    t5 = <Segmented aria-label={t3} value={current} onValueChange={t4} options={options} className="h-9 [&_button]:h-8 [&_button]:min-w-8 [&_button]:px-2.5" />;
    $[6] = current;
    $[7] = options;
    $[8] = t3;
    $[9] = t4;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  return t5;
}

// ── Column-configurable grid (fd-boutique-04) ────────────────────────────────
// Inline template so any column count survives Tailwind purge. `columns` —
// выбор покупателя на компьютере (2/4/6), `mobileColumns` — на телефоне (1/2).
// Figma «Лента»: зазор 20 по обеим осям. `onQuickView` — кнопка «Быстрый
// просмотр» на фото (кадр «Быстрый просмотр карточки товара»): на компьютере
// проявляется при наведении, на телефоне видна всегда.
function _temp5(n) {
  return {
    value: String(n),
    label: String(n)
  };
}
export function ColumnsGrid(t0) {
  const $ = _c(20);
  const {
    products,
    columns,
    mobileColumns: t1,
    onQuickView
  } = t0;
  const mobileColumns = t1 === undefined ? 2 : t1;
  const {
    t
  } = useTranslation();
  const narrow = useNarrow();
  const cols = narrow ? Math.min(mobileColumns, 2) : columns;
  const t2 = cols >= 6 ? "gap-3" : "gap-5";
  let t3;
  if ($[0] !== t2) {
    t3 = cn("grid", t2);
    $[0] = t2;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  const t4 = `repeat(${cols}, minmax(0, 1fr))`;
  let t5;
  if ($[2] !== t4) {
    t5 = {
      gridTemplateColumns: t4
    };
    $[2] = t4;
    $[3] = t5;
  } else {
    t5 = $[3];
  }
  let t6;
  if ($[4] !== cols || $[5] !== narrow || $[6] !== onQuickView || $[7] !== products || $[8] !== t) {
    let t7;
    if ($[10] !== cols || $[11] !== narrow || $[12] !== onQuickView || $[13] !== t) {
      t7 = tile => <div key={tile.key} className="group/qv relative min-w-0"><ProductCard product={tile.product} tile={tile} />{onQuickView && <div className="pointer-events-none absolute inset-x-0 top-0 aspect-[270/359]"><button type="button" onClick={() => onQuickView(tile.product)} aria-label={t("shop.quickView")} title={t("shop.quickView")} data-testid="shop-quick-view" className={cn("pointer-events-auto absolute left-2 flex h-8 items-center gap-1.5 rounded-full bg-white/90 px-2.5 text-[12px] leading-4 font-semibold text-ds-primary shadow-sm backdrop-blur outline-none transition-opacity focus-visible:opacity-100 focus-visible:ring-2 focus-visible:ring-ds-focus md:opacity-0 md:group-hover/qv:opacity-100", tile.stock < 1 && !tile.product.madeToOrder ? "top-10" : "top-2")}><EyeIcon size={16} /><span className={cn(cols >= 6 || narrow ? "sr-only" : undefined)}>{t("shop.quickView")}</span></button></div>}</div>;
      $[10] = cols;
      $[11] = narrow;
      $[12] = onQuickView;
      $[13] = t;
      $[14] = t7;
    } else {
      t7 = $[14];
    }
    t6 = expandProductTiles(products).map(t7);
    $[4] = cols;
    $[5] = narrow;
    $[6] = onQuickView;
    $[7] = products;
    $[8] = t;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[15] !== cols || $[16] !== t3 || $[17] !== t5 || $[18] !== t6) {
    t7 = <div className={t3} style={t5} data-testid="shop-grid" data-columns={cols}>{t6}</div>;
    $[15] = cols;
    $[16] = t3;
    $[17] = t5;
    $[18] = t6;
    $[19] = t7;
  } else {
    t7 = $[19];
  }
  return t7;
}

// ── Interactive block: interior pins hotspots (fd-boutique-07) ────────────────
export function InteriorPinsView(t0) {
  const $ = _c(31);
  const {
    block,
    productById
  } = t0;
  const {
    t
  } = useTranslation();
  const [slideIdx, setSlideIdx] = useState(0);
  const slide = block.slides[slideIdx];
  const [activePinId, setActivePinId] = useState(slide?.pins[0]?.id ?? null);
  if (!slide) {
    return null;
  }
  let t1;
  if ($[0] !== activePinId || $[1] !== slide.pins) {
    t1 = slide.pins.find(p => p.id === activePinId) ?? slide.pins[0];
    $[0] = activePinId;
    $[1] = slide.pins;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const activePin = t1;
  let t2;
  if ($[3] !== activePin || $[4] !== productById) {
    t2 = activePin ? productById(activePin.productId) : undefined;
    $[3] = activePin;
    $[4] = productById;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const activeProduct = t2;
  let t3;
  if ($[6] !== t) {
    t3 = t("shop.interactiveHint");
    $[6] = t;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] !== slide.imageUrl) {
    t4 = <img src={slide.imageUrl} alt="" className="size-full object-cover" />;
    $[8] = slide.imageUrl;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  let t5;
  if ($[10] !== activePinId || $[11] !== slide.pins || $[12] !== t) {
    let t6;
    if ($[14] !== activePinId || $[15] !== t) {
      t6 = pin => <button key={pin.id} type="button" onClick={() => setActivePinId(pin.id)} style={{
        left: `${pin.x}%`,
        top: `${pin.y}%`
      }} aria-label={t("shop.interactiveHint")} className={cn("absolute size-4 -translate-x-1/2 -translate-y-1/2 rounded-full transition-transform hover:scale-110", activePinId === pin.id ? "bg-osmix-tangelo" : "bg-white ring-2 ring-osmix-tangelo/60")} />;
      $[14] = activePinId;
      $[15] = t;
      $[16] = t6;
    } else {
      t6 = $[16];
    }
    t5 = slide.pins.map(t6);
    $[10] = activePinId;
    $[11] = slide.pins;
    $[12] = t;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  let t6;
  if ($[17] !== block.slides || $[18] !== slideIdx) {
    t6 = block.slides.length > 1 && <div className="absolute bottom-3 left-1/2 flex -translate-x-1/2 gap-2">{block.slides.map((s, i) => <button key={s.id} type="button" onClick={() => {
        setSlideIdx(i);
        setActivePinId(s.pins[0]?.id ?? null);
      }} aria-label={`slide ${i + 1}`} className={cn("h-1.5 rounded-full transition-all", i === slideIdx ? "w-6 bg-white" : "w-1.5 bg-white/60")} />)}</div>;
    $[17] = block.slides;
    $[18] = slideIdx;
    $[19] = t6;
  } else {
    t6 = $[19];
  }
  let t7;
  if ($[20] !== t4 || $[21] !== t5 || $[22] !== t6) {
    t7 = <div className="relative aspect-[16/9] w-full overflow-hidden rounded-2xl bg-osmix-gray-100 md:flex-1">{t4}{t5}{t6}</div>;
    $[20] = t4;
    $[21] = t5;
    $[22] = t6;
    $[23] = t7;
  } else {
    t7 = $[23];
  }
  let t8;
  if ($[24] !== activeProduct || $[25] !== t) {
    t8 = <div className="w-full md:w-64 md:shrink-0">{activeProduct ? <ProductCard product={activeProduct} /> : <div className="flex aspect-[3/4] items-center justify-center rounded-xl bg-osmix-gray-100 p-4 text-center type-body2 text-osmix-text-2">{t("shop.interactiveHint")}</div>}</div>;
    $[24] = activeProduct;
    $[25] = t;
    $[26] = t8;
  } else {
    t8 = $[26];
  }
  let t9;
  if ($[27] !== t3 || $[28] !== t7 || $[29] !== t8) {
    t9 = <section aria-label={t3} className="flex flex-col gap-4 md:flex-row md:gap-6">{t7}{t8}</section>;
    $[27] = t3;
    $[28] = t7;
    $[29] = t8;
    $[30] = t9;
  } else {
    t9 = $[30];
  }
  return t9;
}

// ── Interactive block: outfit gallery ────────────────────────────────────────
export function OutfitGalleryView(t0) {
  const $ = _c(24);
  const {
    block,
    productById
  } = t0;
  const [activeIdx, setActiveIdx] = useState(0);
  const active = block.outfits[activeIdx];
  if (!active) {
    return null;
  }
  let t1;
  let t2;
  let t3;
  let t4;
  if ($[0] !== active.imageUrl || $[1] !== active.productIds || $[2] !== activeIdx || $[3] !== block.outfits || $[4] !== productById) {
    const products = active.productIds.map(productById).filter(_temp6).slice(0, 2);
    t3 = "flex flex-col gap-4 md:flex-row md:gap-6";
    let t5;
    if ($[9] !== active.imageUrl) {
      t5 = <img src={active.imageUrl} alt="" className="size-full object-cover" />;
      $[9] = active.imageUrl;
      $[10] = t5;
    } else {
      t5 = $[10];
    }
    let t6;
    if ($[11] !== activeIdx || $[12] !== block.outfits) {
      t6 = block.outfits.length > 1 && <div className="absolute bottom-3 left-1/2 flex -translate-x-1/2 gap-2">{block.outfits.map((o, i) => <button key={o.id} type="button" onClick={() => setActiveIdx(i)} aria-label={`outfit ${i + 1}`} className={cn("h-1.5 rounded-full transition-all", i === activeIdx ? "w-6 bg-white" : "w-1.5 bg-white/60")} />)}</div>;
      $[11] = activeIdx;
      $[12] = block.outfits;
      $[13] = t6;
    } else {
      t6 = $[13];
    }
    if ($[14] !== t5 || $[15] !== t6) {
      t4 = <div className="relative aspect-[3/4] w-full overflow-hidden rounded-2xl bg-osmix-gray-100 md:aspect-[4/5] md:w-1/2">{t5}{t6}</div>;
      $[14] = t5;
      $[15] = t6;
      $[16] = t4;
    } else {
      t4 = $[16];
    }
    t1 = "grid flex-1 grid-cols-2 gap-4";
    t2 = products.map(_temp7);
    $[0] = active.imageUrl;
    $[1] = active.productIds;
    $[2] = activeIdx;
    $[3] = block.outfits;
    $[4] = productById;
    $[5] = t1;
    $[6] = t2;
    $[7] = t3;
    $[8] = t4;
  } else {
    t1 = $[5];
    t2 = $[6];
    t3 = $[7];
    t4 = $[8];
  }
  let t5;
  if ($[17] !== t1 || $[18] !== t2) {
    t5 = <div className={t1}>{t2}</div>;
    $[17] = t1;
    $[18] = t2;
    $[19] = t5;
  } else {
    t5 = $[19];
  }
  let t6;
  if ($[20] !== t3 || $[21] !== t4 || $[22] !== t5) {
    t6 = <section className={t3}>{t4}{t5}</section>;
    $[20] = t3;
    $[21] = t4;
    $[22] = t5;
    $[23] = t6;
  } else {
    t6 = $[23];
  }
  return t6;
}

// ── Interactive block: about-brand ───────────────────────────────────────────
function _temp7(p_0) {
  return <ProductCard key={p_0.id} product={p_0} />;
}
function _temp6(p) {
  return Boolean(p);
}
export function AboutBrandView(t0) {
  const $ = _c(12);
  const {
    block
  } = t0;
  let t1;
  if ($[0] !== block.photoUrl) {
    t1 = block.photoUrl && <div className="aspect-[4/5] overflow-hidden rounded-xl"><img src={block.photoUrl} alt="" className="size-full object-cover grayscale" /></div>;
    $[0] = block.photoUrl;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== block.heading) {
    t2 = <h2 className="type-h2 text-osmix-text-1">{block.heading}</h2>;
    $[2] = block.heading;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== block.body) {
    t3 = block.body && <p className="type-body1 text-osmix-text-2">{block.body}</p>;
    $[4] = block.body;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== t2 || $[7] !== t3) {
    t4 = <div className="flex flex-col gap-4">{t2}{t3}</div>;
    $[6] = t2;
    $[7] = t3;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== t1 || $[10] !== t4) {
    t5 = <section className="grid items-center gap-6 rounded-2xl bg-card p-6 md:grid-cols-2 md:p-10">{t1}{t4}</section>;
    $[9] = t1;
    $[10] = t4;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  return t5;
}

// ── Блоки «О компании» из конструктора (fd-boutique-08) ─────────────────────
// Выводит включённые блоки `config.about` — только если продавец включил
// «О компании» тумблером конструктора. Таймлайн истории, три преимущества и
// процесс изготовления — из пунктов, которые заполнил продавец; пустые блоки
// не рисуем (без выдуманного текста). Фото-блоки без своих картинок берут
// фото товаров магазина, как раньше.
export function AboutBlocks(t0) {
  const $ = _c(21);
  const {
    config,
    shop,
    photos: t1
  } = t0;
  let t2;
  if ($[0] !== t1) {
    t2 = t1 === undefined ? [] : t1;
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const photos = t2;
  const {
    t
  } = useTranslation();
  if (!config.about.enabled) {
    return null;
  }
  const t3 = config.about.blocks;
  let t4;
  let t5;
  let t6;
  let t7;
  if ($[2] !== config.about.blocks || $[3] !== config.about.titlePageEnabled || $[4] !== photos || $[5] !== shop || $[6] !== t) {
    t7 = Symbol.for("react.early_return_sentinel");
    bb0: {
      let t8;
      if ($[11] !== config.about.titlePageEnabled) {
        t8 = b => b.enabled && (b.kind !== "title-page" || config.about.titlePageEnabled);
        $[11] = config.about.titlePageEnabled;
        $[12] = t8;
      } else {
        t8 = $[12];
      }
      const blocks = t3.filter(t8);
      if (blocks.length === 0) {
        t7 = null;
        break bb0;
      }
      t4 = "flex flex-col gap-14";
      t5 = "shop-about-blocks";
      let t9;
      if ($[13] !== photos || $[14] !== shop || $[15] !== t) {
        t9 = block => <AboutBlockView key={block.id} block={block} shop={shop} photos={photos} t={t} />;
        $[13] = photos;
        $[14] = shop;
        $[15] = t;
        $[16] = t9;
      } else {
        t9 = $[16];
      }
      t6 = blocks.map(t9);
    }
    $[2] = config.about.blocks;
    $[3] = config.about.titlePageEnabled;
    $[4] = photos;
    $[5] = shop;
    $[6] = t;
    $[7] = t4;
    $[8] = t5;
    $[9] = t6;
    $[10] = t7;
  } else {
    t4 = $[7];
    t5 = $[8];
    t6 = $[9];
    t7 = $[10];
  }
  if (t7 !== Symbol.for("react.early_return_sentinel")) {
    return t7;
  }
  let t8;
  if ($[17] !== t4 || $[18] !== t5 || $[19] !== t6) {
    t8 = <div className={t4} data-testid={t5}>{t6}</div>;
    $[17] = t4;
    $[18] = t5;
    $[19] = t6;
    $[20] = t8;
  } else {
    t8 = $[20];
  }
  return t8;
}
const h2 = "text-[24px] leading-[30px] font-bold tracking-[-0.6px] text-ds-ink md:text-[28px] md:leading-[34px]";
const lead = "max-w-3xl text-[16px] leading-[22px] text-ds-secondary";
function AboutBlockView(t0) {
  const $ = _c(26);
  const {
    block,
    shop,
    photos,
    t
  } = t0;
  let t1;
  if ($[0] !== block.title) {
    t1 = block.title?.trim();
    $[0] = block.title;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const title = t1;
  let t2;
  if ($[2] !== block.description) {
    t2 = block.description?.trim();
    $[2] = block.description;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const description = t2;
  const t3 = block.cards;
  let t4;
  if ($[4] !== block.cards || $[5] !== block.id || $[6] !== block.images || $[7] !== block.kind || $[8] !== description || $[9] !== photos || $[10] !== shop || $[11] !== t || $[12] !== title) {
    t4 = Symbol.for("react.early_return_sentinel");
    bb0: {
      const cards = t3.filter(_temp8);
      const images = block.images.length ? block.images : photos.slice(0, block.kind === "cards-6" ? 6 : block.kind === "large-photo" ? 1 : 4);
      let t5;
      if ($[14] !== description || $[15] !== t || $[16] !== title) {
        t5 = fallbackKey => <div className="flex flex-col gap-2"><h2 className={h2}>{title || t(fallbackKey)}</h2>{description && <p className={lead}>{description}</p>}</div>;
        $[14] = description;
        $[15] = t;
        $[16] = title;
        $[17] = t5;
      } else {
        t5 = $[17];
      }
      const heading = t5;
      switch (block.kind) {
        case "timeline":
          {
            t4 = cards.length ? <section className="flex flex-col gap-6" data-testid="about-timeline">{heading("shop.aboutBlocks.timeline")}<ol className="relative ml-2 flex flex-col gap-7 border-l border-ds-line-2 pl-6">{cards.map(_temp9)}</ol></section> : null;
            break bb0;
          }
        case "advantages":
          {
            t4 = cards.length ? <section className="flex flex-col gap-6" data-testid="about-advantages">{heading("shop.aboutBlocks.advantages")}<div className="grid gap-4 md:grid-cols-3">{cards.slice(0, 3).map(_temp0)}</div></section> : null;
            break bb0;
          }
        case "process":
          {
            t4 = cards.length ? <section className="flex flex-col gap-6" data-testid="about-process">{heading("shop.aboutBlocks.process")}<ol className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">{cards.map(_temp1)}</ol></section> : null;
            break bb0;
          }
        case "title-page":
          {
            const t6 = title || shop.name;
            let t7;
            if ($[18] !== t6) {
              t7 = <h2 className="max-w-3xl text-[28px] leading-[34px] font-bold tracking-[-0.6px] text-ds-ink md:text-[36px] md:leading-[42px]">{t6}</h2>;
              $[18] = t6;
              $[19] = t7;
            } else {
              t7 = $[19];
            }
            let t8;
            if ($[20] !== description || $[21] !== shop) {
              t8 = (description || shop.description) && <p className={cn(lead, "max-w-2xl")}>{description || shop.description}</p>;
              $[20] = description;
              $[21] = shop;
              $[22] = t8;
            } else {
              t8 = $[22];
            }
            let t9;
            if ($[23] !== t7 || $[24] !== t8) {
              t9 = <section className="flex flex-col items-center gap-4 text-center">{t7}{t8}</section>;
              $[23] = t7;
              $[24] = t8;
              $[25] = t9;
            } else {
              t9 = $[25];
            }
            t4 = t9;
            break bb0;
          }
        case "large-photo":
          {
            t4 = images[0] ? <section className="flex flex-col gap-4">{title && <h2 className={h2}>{title}</h2>}<div className="aspect-[21/9] w-full overflow-hidden rounded-2xl bg-osmix-gray-100"><img src={images[0]} alt={title ?? ""} className="size-full object-cover" /></div>{description && <p className={lead}>{description}</p>}</section> : null;
            break bb0;
          }
        case "cards-6":
          {
            const items = cards.length ? cards : images.slice(0, 6).map((img_0, i_0) => ({
              id: `${block.id}-${i_0}`,
              imageUrl: img_0,
              subtitle: undefined,
              description: undefined
            }));
            t4 = items.length ? <section className="flex flex-col gap-6">{(title || description) && <div className="flex flex-col gap-2">{title && <h2 className={h2}>{title}</h2>}{description && <p className={lead}>{description}</p>}</div>}<div className="grid grid-cols-2 gap-4 md:grid-cols-3">{items.map(_temp10)}</div></section> : null;
            break bb0;
          }
        default:
          {
            t4 = images.length ? <section className="flex flex-col gap-4">{title && <h2 className={h2}>{title}</h2>}{description && <p className={lead}>{description}</p>}<div className="flex gap-4 overflow-x-auto pb-2">{images.map((img, i) => <div key={`${block.id}-${i}`} className="aspect-[3/4] w-44 shrink-0 overflow-hidden rounded-xl bg-osmix-gray-100 md:w-56"><img src={img} alt="" className="size-full object-cover" /></div>)}</div></section> : null;
            break bb0;
          }
      }
    }
    $[4] = block.cards;
    $[5] = block.id;
    $[6] = block.images;
    $[7] = block.kind;
    $[8] = description;
    $[9] = photos;
    $[10] = shop;
    $[11] = t;
    $[12] = title;
    $[13] = t4;
  } else {
    t4 = $[13];
  }
  if (t4 !== Symbol.for("react.early_return_sentinel")) {
    return t4;
  }
}

// ── Новости магазина (fd-boutique-09) ────────────────────────────────────────
// Продавец пишет новость в конструкторе: дата, заголовок, текст, фото.
// Свежие сверху; на «Главной» — три последних, в «О магазине» — все.
function _temp10(card) {
  return <div key={card.id} className="flex flex-col gap-3">{card.imageUrl && <div className="aspect-[4/3] overflow-hidden rounded-xl bg-osmix-gray-100"><img src={card.imageUrl} alt={card.subtitle ?? ""} className="size-full object-cover" /></div>}{card.subtitle && <p className="text-[16px] leading-[22px] font-semibold text-ds-primary">{card.subtitle}</p>}{card.description && <p className="text-[14px] leading-5 text-ds-secondary">{card.description}</p>}</div>;
}
function _temp1(c_0, i_1) {
  return <li key={c_0.id} className="flex flex-col gap-3">{c_0.imageUrl && <img src={c_0.imageUrl} alt="" className="aspect-[4/3] w-full rounded-[12px] object-cover" />}<span className="flex items-center gap-2"><span className="flex size-8 shrink-0 items-center justify-center rounded-full bg-ds-primary text-[14px] font-bold text-white">{c_0.label || i_1 + 1}</span>{c_0.subtitle && <span className="text-[16px] leading-[22px] font-semibold text-ds-primary">{c_0.subtitle}</span>}</span>{c_0.description && <p className="text-[14px] leading-5 text-ds-secondary">{c_0.description}</p>}</li>;
}
function _temp0(c_1, i_2) {
  return <div key={c_1.id} className="flex flex-col gap-3 rounded-[16px] bg-ds-quaternary p-5">{c_1.imageUrl ? <img src={c_1.imageUrl} alt="" className="aspect-[4/3] w-full rounded-[12px] object-cover" /> : <span className="flex size-10 items-center justify-center rounded-full bg-ds-accent text-[16px] font-bold text-white">{c_1.label || i_2 + 1}</span>}{c_1.subtitle && <span className="text-[16px] leading-[22px] font-bold text-ds-primary">{c_1.subtitle}</span>}{c_1.description && <p className="text-[14px] leading-5 text-ds-secondary">{c_1.description}</p>}</div>;
}
function _temp9(c_2) {
  return <li key={c_2.id} className="relative flex flex-col gap-1.5"><span className="absolute top-1 -left-[31px] size-3 rounded-full border-2 border-white bg-ds-accent ring-1 ring-ds-accent" />{c_2.label && <span className="text-[14px] leading-[18px] font-bold text-ds-accent">{c_2.label}</span>}{c_2.subtitle && <span className="text-[16px] leading-[22px] font-semibold text-ds-primary">{c_2.subtitle}</span>}{c_2.description && <p className="max-w-2xl text-[14px] leading-5 text-ds-secondary">{c_2.description}</p>}{c_2.imageUrl && <img src={c_2.imageUrl} alt="" className="mt-1 aspect-[16/9] w-full max-w-md rounded-[12px] object-cover" />}</li>;
}
function _temp8(c) {
  return c.subtitle?.trim() || c.description?.trim() || c.imageUrl;
}
const ruDay = (ymd: string) => ymd.slice(0, 10).split("-").reverse().join(".");
export function NewsBlock(t0) {
  const $ = _c(23);
  const {
    news,
    limit
  } = t0;
  const {
    t
  } = useTranslation();
  const [expanded, setExpanded] = useState(null);
  let T0;
  let t1;
  let t2;
  let t3;
  let t4;
  let t5;
  if ($[0] !== expanded || $[1] !== limit || $[2] !== news || $[3] !== t) {
    t5 = Symbol.for("react.early_return_sentinel");
    bb0: {
      const items = [...news].sort(_temp11).slice(0, limit ?? news.length);
      if (items.length === 0) {
        t5 = null;
        break bb0;
      }
      T0 = Section;
      if ($[10] !== t) {
        t4 = t("shop.news.title");
        $[10] = t;
        $[11] = t4;
      } else {
        t4 = $[11];
      }
      t1 = "grid gap-5 md:grid-cols-3";
      t2 = "shop-news";
      let t6;
      if ($[12] !== expanded || $[13] !== t) {
        t6 = item => <article key={item.id} className="flex flex-col gap-3">{item.imageUrl && <div className="aspect-[16/9] overflow-hidden rounded-[16px] bg-ds-quaternary"><img src={item.imageUrl} alt="" className="size-full object-cover" loading="lazy" /></div>}<time dateTime={item.date} className="text-[12px] leading-4 font-medium text-ds-muted">{ruDay(item.date)}</time><h3 className="text-[18px] leading-6 font-bold text-ds-primary">{item.title}</h3>{item.body && <><p className={cn("text-[14px] leading-5 whitespace-pre-line text-ds-secondary", expanded !== item.id && "line-clamp-4")}>{item.body}</p>{item.body.length > 220 && <Button variant="text" className="self-start" onClick={() => setExpanded(id => id === item.id ? null : item.id)}>{expanded === item.id ? t("shop.info.showLess") : t("shop.news.readMore")}</Button>}</>}</article>;
        $[12] = expanded;
        $[13] = t;
        $[14] = t6;
      } else {
        t6 = $[14];
      }
      t3 = items.map(t6);
    }
    $[0] = expanded;
    $[1] = limit;
    $[2] = news;
    $[3] = t;
    $[4] = T0;
    $[5] = t1;
    $[6] = t2;
    $[7] = t3;
    $[8] = t4;
    $[9] = t5;
  } else {
    T0 = $[4];
    t1 = $[5];
    t2 = $[6];
    t3 = $[7];
    t4 = $[8];
    t5 = $[9];
  }
  if (t5 !== Symbol.for("react.early_return_sentinel")) {
    return t5;
  }
  let t6;
  if ($[15] !== t1 || $[16] !== t2 || $[17] !== t3) {
    t6 = <div className={t1} data-testid={t2}>{t3}</div>;
    $[15] = t1;
    $[16] = t2;
    $[17] = t3;
    $[18] = t6;
  } else {
    t6 = $[18];
  }
  let t7;
  if ($[19] !== T0 || $[20] !== t4 || $[21] !== t6) {
    t7 = <T0 title={t4}>{t6}</T0>;
    $[19] = T0;
    $[20] = t4;
    $[21] = t6;
    $[22] = t7;
  } else {
    t7 = $[22];
  }
  return t7;
}

// ── Сезонная акция (fd-boutique-09) ──────────────────────────────────────────
// Обложка акции из конструктора + подборка товаров магазина со скидкой (SALE
// на шлюзе). Нет товаров со скидкой — блок не показываем: акция без товаров
// была бы просто картинкой.
function _temp11(a, b) {
  return a.date < b.date ? 1 : -1;
}
export function SeasonalSaleBlock(t0) {
  const $ = _c(47);
  const {
    shopId,
    sale,
    columns,
    onQuickView,
    onShowAll
  } = t0;
  const {
    t
  } = useTranslation();
  let t1;
  if ($[0] !== columns || $[1] !== shopId) {
    t1 = {
      queryKey: ["products", "list", shopId, "sale-block", columns],
      queryFn: () => api.products.list({
        shopId,
        sale: true,
        sort: "popular",
        pageSize: Math.max(4, columns) * 2
      })
    };
    $[0] = columns;
    $[1] = shopId;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const products = useQuery(t1);
  let t2;
  if ($[3] !== products.data?.data) {
    t2 = products.data?.data ?? [];
    $[3] = products.data?.data;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const items = t2;
  if (items.length === 0) {
    return null;
  }
  let t3;
  if ($[5] !== sale.imageUrl) {
    t3 = sale.imageUrl && <img src={sale.imageUrl} alt="" className="absolute inset-0 size-full object-cover" />;
    $[5] = sale.imageUrl;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== sale.imageUrl) {
    t4 = sale.imageUrl && <div className="absolute inset-0 bg-gradient-to-r from-black/55 via-black/25 to-transparent" />;
    $[7] = sale.imageUrl;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  const t5 = sale.imageUrl ? "text-white" : "text-ds-ink";
  let t6;
  if ($[9] !== t5) {
    t6 = cn("relative flex flex-col justify-center gap-2 p-6 md:p-10", t5);
    $[9] = t5;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  let t7;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <span className="text-[13px] leading-4 font-bold tracking-[0.2em] uppercase opacity-80">SALE</span>;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  let t8;
  if ($[12] !== sale.title || $[13] !== t) {
    t8 = sale.title || t("shop.seasonal");
    $[12] = sale.title;
    $[13] = t;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  let t9;
  if ($[15] !== t8) {
    t9 = <h2 className="max-w-xl text-[28px] leading-[32px] font-bold tracking-[-0.6px] md:text-[40px] md:leading-[44px]">{t8}</h2>;
    $[15] = t8;
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  let t10;
  if ($[17] !== sale.description) {
    t10 = sale.description && <p className="max-w-lg text-[15px] leading-[21px] opacity-90">{sale.description}</p>;
    $[17] = sale.description;
    $[18] = t10;
  } else {
    t10 = $[18];
  }
  const t11 = sale.imageUrl ? "gray" : "dark";
  const t12 = sale.imageUrl && "bg-white text-ds-primary hover:bg-white/90";
  let t13;
  if ($[19] !== t12) {
    t13 = cn("mt-2 self-start", t12);
    $[19] = t12;
    $[20] = t13;
  } else {
    t13 = $[20];
  }
  let t14;
  if ($[21] !== t) {
    t14 = t("shop.saleAll");
    $[21] = t;
    $[22] = t14;
  } else {
    t14 = $[22];
  }
  let t15;
  if ($[23] !== onShowAll || $[24] !== t11 || $[25] !== t13 || $[26] !== t14) {
    t15 = <Button variant={t11} size="md" onClick={onShowAll} className={t13}>{t14}</Button>;
    $[23] = onShowAll;
    $[24] = t11;
    $[25] = t13;
    $[26] = t14;
    $[27] = t15;
  } else {
    t15 = $[27];
  }
  let t16;
  if ($[28] !== t10 || $[29] !== t15 || $[30] !== t6 || $[31] !== t9) {
    t16 = <div className={t6}>{t7}{t9}{t10}{t15}</div>;
    $[28] = t10;
    $[29] = t15;
    $[30] = t6;
    $[31] = t9;
    $[32] = t16;
  } else {
    t16 = $[32];
  }
  let t17;
  if ($[33] !== t16 || $[34] !== t3 || $[35] !== t4) {
    t17 = <div className="relative flex min-h-[180px] w-full overflow-hidden rounded-[20px] bg-[linear-gradient(112deg,#E8E7E3_43%,#E0DFDB_100%)] md:min-h-[260px]">{t3}{t4}{t16}</div>;
    $[33] = t16;
    $[34] = t3;
    $[35] = t4;
    $[36] = t17;
  } else {
    t17 = $[36];
  }
  let t18;
  if ($[37] !== columns || $[38] !== items) {
    t18 = items.slice(0, Math.max(4, columns) * 2);
    $[37] = columns;
    $[38] = items;
    $[39] = t18;
  } else {
    t18 = $[39];
  }
  let t19;
  if ($[40] !== columns || $[41] !== onQuickView || $[42] !== t18) {
    t19 = <ColumnsGrid products={t18} columns={columns} onQuickView={onQuickView} />;
    $[40] = columns;
    $[41] = onQuickView;
    $[42] = t18;
    $[43] = t19;
  } else {
    t19 = $[43];
  }
  let t20;
  if ($[44] !== t17 || $[45] !== t19) {
    t20 = <section className="flex flex-col gap-6" data-testid="shop-seasonal-sale">{t17}{t19}</section>;
    $[44] = t17;
    $[45] = t19;
    $[46] = t20;
  } else {
    t20 = $[46];
  }
  return t20;
}

// ── Simple info tabs: delivery & custom order ────────────────────────────────
export function DeliveryTab() {
  const $ = _c(30);
  const {
    t
  } = useTranslation();
  let t0;
  if ($[0] !== t) {
    t0 = t("shop.deliveryTab.guaranteesTitle");
    $[0] = t;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== t0) {
    t1 = <h3 className="type-h3-bold text-osmix-text-1">{t0}</h3>;
    $[2] = t0;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  let t2;
  if ($[4] !== t) {
    t2 = t("shop.deliveryTab.guaranteesBody");
    $[4] = t;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== t2) {
    t3 = <p className="type-body1 text-osmix-text-2">{t2}</p>;
    $[6] = t2;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] !== t1 || $[9] !== t3) {
    t4 = <div className="flex flex-col gap-3 rounded-2xl border border-osmix-gray-300 p-6">{t1}{t3}</div>;
    $[8] = t1;
    $[9] = t3;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  let t5;
  if ($[11] !== t) {
    t5 = t("shop.deliveryTab.deliveryTitle");
    $[11] = t;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  let t6;
  if ($[13] !== t5) {
    t6 = <h3 className="type-h3-bold text-osmix-text-1">{t5}</h3>;
    $[13] = t5;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  let t7;
  if ($[15] !== t) {
    t7 = t("shop.deliveryTab.standard");
    $[15] = t;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  let t8;
  if ($[17] !== t7) {
    t8 = <p className="type-body1 text-osmix-text-2">{t7}</p>;
    $[17] = t7;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  let t9;
  if ($[19] !== t) {
    t9 = t("shop.deliveryTab.express");
    $[19] = t;
    $[20] = t9;
  } else {
    t9 = $[20];
  }
  let t10;
  if ($[21] !== t9) {
    t10 = <p className="type-body1 text-osmix-text-2">{t9}</p>;
    $[21] = t9;
    $[22] = t10;
  } else {
    t10 = $[22];
  }
  let t11;
  if ($[23] !== t10 || $[24] !== t6 || $[25] !== t8) {
    t11 = <div className="flex flex-col gap-3 rounded-2xl border border-osmix-gray-300 p-6">{t6}{t8}{t10}</div>;
    $[23] = t10;
    $[24] = t6;
    $[25] = t8;
    $[26] = t11;
  } else {
    t11 = $[26];
  }
  let t12;
  if ($[27] !== t11 || $[28] !== t4) {
    t12 = <div className="grid gap-6 md:grid-cols-2">{t4}{t11}</div>;
    $[27] = t11;
    $[28] = t4;
    $[29] = t12;
  } else {
    t12 = $[29];
  }
  return t12;
}

// ── Rating chip helper ───────────────────────────────────────────────────────
export function RatingChip(t0) {
  const $ = _c(5);
  const {
    value
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <StarIcon className="text-osmix-yellow" />;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== value) {
    t2 = value.toFixed(1);
    $[1] = value;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== t2) {
    t3 = <span className="flex h-6 items-center gap-1 rounded bg-osmix-gray-100 px-2 type-body2-bold text-osmix-text-1">{t1}{t2}</span>;
    $[3] = t2;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  return t3;
}

// ── Section wrapper ──────────────────────────────────────────────────────────
export function Section(t0) {
  const $ = _c(8);
  const {
    title,
    action,
    children
  } = t0;
  let t1;
  if ($[0] !== title) {
    t1 = <h2 className="type-h2 text-osmix-text-1">{title}</h2>;
    $[0] = title;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== action || $[3] !== t1) {
    t2 = <div className="flex items-center justify-between gap-4">{t1}{action}</div>;
    $[2] = action;
    $[3] = t1;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== children || $[6] !== t2) {
    t3 = <section className="flex flex-col gap-4">{t2}{children}</section>;
    $[5] = children;
    $[6] = t2;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  return t3;
}